import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Workflow, Plus, Trash2, Save, ArrowLeft, AlertTriangle, HelpCircle } from 'lucide-react';

import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { AppRoutes } from '@/constants/routes';
import {
  postingRuleApi, ACCOUNT_SOURCES, TARGET_DOCS, ITERATORS,
  type SaveConditionDto, type SaveLineDto, type SavePostingRuleRequest,
} from '@/api/postingRule.api';
import { integrationMappingApi, type IntAccountDto } from '@/api/integrationMapping.api';
import { ACCOUNT_ROLES } from '@/api/roleAccount.api';

const OPERATORS = ['=', '!=', 'IN', 'NOT_IN', '>', '>=', '<', '<='];
const DRCR = [{ v: 'D', l: 'Dr' }, { v: 'C', l: 'Cr' }, { v: 'BALANCE', l: 'Balance' }];

// Engine roles the account-role map can resolve, offered as typeahead suggestions.
const RULE_ROLES = [...new Set([
  ...ACCOUNT_ROLES, 'GRNI', 'INPUT_TAX', 'OUTPUT_TAX', 'CARD_CLEARING', 'CLEARING',
  'FX_GAIN', 'FX_LOSS', 'UNREALIZED_FX_GAIN', 'UNREALIZED_FX_LOSS', 'ROUNDING',
  'CUSTOMER_DEPOSIT', 'BOTTLE_DEPOSIT_LIABILITY',
])].sort();

// Common amount bases / formula fields for the amount typeahead.
const AMOUNT_BASES = [
  'totals.net', 'totals.grandTotal', 'totals.goodsNet', 'totals.taxTotal', 'totals.cogsTotal',
  'totals.amount', 'totals.discount', 'tender.amount', 'category.net', 'category.taxTotal',
  'line.amount', 'line.taxAmount', 'line.cost',
];

// Context fields + functions available to formulas / include-when expressions (reference panel).
const FORMULA_REFERENCE: { group: string; items: string[] }[] = [
  { group: 'Totals', items: ['totals.net', 'totals.taxTotal', 'totals.cogsTotal', 'totals.grandTotal', 'totals.goodsNet', 'totals.amount', 'totals.discount'] },
  { group: 'Iterated rows', items: ['tender.amount', 'tender.modeCode', 'category.net', 'category.categoryExternalId', 'line.amount', 'line.cost'] },
  { group: 'Event / party', items: ['party.type', 'party.vendorType', 'party.externalId', 'event.currencyCode', 'event.businessDate'] },
  { group: 'Operators', items: ['=', '!=', 'IN', 'NOT_IN', '>', '>=', '<', '<=', '+ - * /', '( )'] },
];

const emptyLine = (n: number): SaveLineDto => ({
  lineNumber: n, drCr: 'D', amountBasis: '', includeWhen: '', iterator: '',
  accountSource: 'ACCOUNT_ROLE_MAP', accountRole: '', accountRef: '', partySource: 'NONE', descriptionTemplate: '',
});

const inputCls = 'rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm text-slate-900 dark:text-white focus:border-signal focus:ring-1 focus:ring-signal outline-none';

export const RuleBuilderPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = !!id;

  const [loading, setLoading] = useState(editing);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [accounts, setAccounts] = useState<IntAccountDto[]>([]);
  const [showRef, setShowRef]   = useState(false);

  const [h, setH] = useState<SavePostingRuleRequest>({
    ruleCode: '', ruleName: '', description: '', sourceEventType: '', targetDocumentType: 'GL_JOURNAL',
    ruleType: 'PRIMARY', priority: 100, stopOnMatch: true, aggregationPolicy: 'NONE', fxGainLossMode: 'AUTO',
    governanceStatus: 'Active', isActive: true, conditions: [], lines: [emptyLine(1)], effectiveFrom: '',
  });

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const d = await postingRuleApi.get(id);
      if (d) setH({
        ruleCode: d.ruleCode, ruleName: d.ruleName, description: d.description ?? '',
        sourceEventType: d.sourceEventType, targetDocumentType: d.targetDocumentType, ruleType: d.ruleType,
        priority: d.priority, stopOnMatch: d.stopOnMatch, aggregationPolicy: d.aggregationPolicy,
        fxGainLossMode: d.fxGainLossMode, governanceStatus: d.governanceStatus, isActive: d.isActive,
        conditions: d.conditions.map(c => ({ ...c })),
        lines: d.lines.map(l => ({ ...l })),
        effectiveFrom: d.effectiveFrom ?? '',
      });
    } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { integrationMappingApi.accounts().then(setAccounts).catch(() => {}); }, []);

  const set = (patch: Partial<SavePostingRuleRequest>) => setH(prev => ({ ...prev, ...patch }));
  const setLine = (i: number, patch: Partial<SaveLineDto>) =>
    setH(prev => ({ ...prev, lines: prev.lines.map((l, j) => j === i ? { ...l, ...patch } : l) }));
  const setCond = (i: number, patch: Partial<SaveConditionDto>) =>
    setH(prev => ({ ...prev, conditions: prev.conditions.map((c, j) => j === i ? { ...c, ...patch } : c) }));

  const save = async () => {
    setError(null);
    if (!h.ruleCode.trim() || !h.sourceEventType.trim()) { setError('Rule code and source event type are required.'); return; }
    if (h.lines.length === 0 || h.lines.some(l => !l.amountBasis.trim())) { setError('Every line needs an amount basis.'); return; }
    setSaving(true);
    try {
      // For a FIXED account the entered code belongs in accountRef (what the engine reads).
      const payload = {
        ...h,
        effectiveFrom: h.effectiveFrom || null,   // empty = today (new) / unchanged (edit); "" is not a date
        lines: h.lines.map((l, i) => {
          const line = { ...l, lineNumber: i + 1 };
          if (line.accountSource === 'FIXED' && !line.accountRef) line.accountRef = line.accountRole;
          return line;
        }),
      };
      if (editing) await postingRuleApi.update(id!, payload);
      else await postingRuleApi.create(payload);
      navigate(AppRoutes.INTEGRATION_POSTING_RULES);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Save failed.');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>;

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center gap-3">
        <button onClick={() => navigate(AppRoutes.INTEGRATION_POSTING_RULES)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Workflow size={20} className="text-signal" /> {editing ? `Edit rule ${h.ruleCode}` : 'New posting rule'}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Define the double-entry a business event should post.</p>
        </div>
        <button onClick={save} disabled={saving}
          className="ml-auto px-4 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg disabled:opacity-50 flex items-center gap-1.5">
          {saving ? <><Spinner size="sm" /> Saving…</> : <><Save size={15} /> Save rule</>}
        </button>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-5xl space-y-5">
          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 dark:border-rose-900/40 dark:bg-rose-900/20 px-4 py-2.5 text-sm text-rose-700 dark:text-rose-300">
              <AlertTriangle size={15} /> {error}
            </div>
          )}

          {/* Header */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-card grid grid-cols-2 md:grid-cols-3 gap-3">
            <label className="flex flex-col gap-1"><span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Rule code</span>
              <input className={inputCls} value={h.ruleCode} disabled={editing} onChange={e => set({ ruleCode: e.target.value })} placeholder="POS_DAILY" /></label>
            <label className="flex flex-col gap-1"><span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Rule name</span>
              <input className={inputCls} value={h.ruleName} onChange={e => set({ ruleName: e.target.value })} placeholder="Retail day summary" /></label>
            <label className="flex flex-col gap-1"><span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Source event type</span>
              <input className={inputCls} value={h.sourceEventType} onChange={e => set({ sourceEventType: e.target.value })} placeholder="pos_sales.daily_summary" /></label>
            <label className="flex flex-col gap-1"><span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Target document</span>
              <select className={inputCls} value={h.targetDocumentType} onChange={e => set({ targetDocumentType: e.target.value })}>{TARGET_DOCS.map(t => <option key={t}>{t}</option>)}</select></label>
            <label className="flex flex-col gap-1"><span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Rule type</span>
              <select className={inputCls} value={h.ruleType} onChange={e => set({ ruleType: e.target.value })}>{['PRIMARY', 'SUPPLEMENTARY'].map(t => <option key={t}>{t}</option>)}</select></label>
            <label className="flex flex-col gap-1"><span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Priority</span>
              <input type="number" className={inputCls} value={h.priority} onChange={e => set({ priority: Number(e.target.value) })} /></label>
            <label className="flex flex-col gap-1"><span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Status</span>
              <select className={inputCls} value={h.governanceStatus} onChange={e => set({ governanceStatus: e.target.value })}>{['Draft', 'Active', 'Suspended'].map(t => <option key={t}>{t}</option>)}</select></label>
            <label className="flex flex-col gap-1"><span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Aggregation</span>
              <select className={inputCls} value={h.aggregationPolicy} onChange={e => set({ aggregationPolicy: e.target.value })}>{['NONE', 'BY_STORE_DAY', 'BY_SHIFT', 'BY_TERMINAL'].map(t => <option key={t}>{t}</option>)}</select></label>
            <label className="flex flex-col gap-1"><span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Effective from</span>
              <input type="date" className={inputCls} value={h.effectiveFrom ?? ''} onChange={e => set({ effectiveFrom: e.target.value })}
                title="First business date this rule applies to. Leave empty for today." /></label>
            <label className="flex items-center gap-2 mt-5"><input type="checkbox" checked={h.isActive} onChange={e => set({ isActive: e.target.checked })} className="h-4 w-4 rounded border-slate-300 dark:border-gray-600 text-signal focus:ring-signal" /><span className="text-sm text-slate-700 dark:text-slate-200">Active</span></label>
          </div>

          {/* Conditions */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-card">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-slate-800 dark:text-white">When (conditions — all must match)</h2>
              <button onClick={() => set({ conditions: [...h.conditions, { attribute: '', operator: '=', value: '' }] })}
                className="text-xs font-medium text-signal hover:underline flex items-center gap-1"><Plus size={13} /> Add condition</button>
            </div>
            {h.conditions.length === 0 ? <p className="text-xs text-slate-400">No conditions — the rule fires for every event of this type.</p> :
              <div className="space-y-2">
                {h.conditions.map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input className={cn(inputCls, 'flex-1')} placeholder="party.vendorType" value={c.attribute} onChange={e => setCond(i, { attribute: e.target.value })} />
                    <select className={inputCls} value={c.operator} onChange={e => setCond(i, { operator: e.target.value })}>{OPERATORS.map(o => <option key={o}>{o}</option>)}</select>
                    <input className={cn(inputCls, 'flex-1')} placeholder='"own" or ["a","b"]' value={c.value ?? ''} onChange={e => setCond(i, { value: e.target.value })} />
                    <button onClick={() => set({ conditions: h.conditions.filter((_, j) => j !== i) })} className="p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>}
          </div>

          {/* Lines */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-card">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-slate-800 dark:text-white">Post these lines</h2>
              <button onClick={() => set({ lines: [...h.lines, emptyLine(h.lines.length + 1)] })}
                className="text-xs font-medium text-signal hover:underline flex items-center gap-1"><Plus size={13} /> Add line</button>
            </div>
            <div className="space-y-2">
              {h.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <select className={cn(inputCls, 'col-span-1')} value={l.drCr} onChange={e => setLine(i, { drCr: e.target.value })}>{DRCR.map(d => <option key={d.v} value={d.v}>{d.l}</option>)}</select>
                  <input list="rb-amounts" className={cn(inputCls, 'col-span-3')} placeholder="amount (e.g. totals.net)" value={l.amountBasis} onChange={e => setLine(i, { amountBasis: e.target.value })} />
                  <select className={cn(inputCls, 'col-span-2')} value={l.accountSource} onChange={e => setLine(i, { accountSource: e.target.value })}>{ACCOUNT_SOURCES.map(s => <option key={s}>{s}</option>)}</select>
                  {l.accountSource === 'FIXED' ? (
                    <input list="rb-accounts" className={cn(inputCls, 'col-span-2')} placeholder="account code"
                      value={l.accountRef ?? ''} onChange={e => setLine(i, { accountRef: e.target.value })} title="Pick a specific GL account" />
                  ) : (
                    <input list="rb-roles" className={cn(inputCls, 'col-span-2')} placeholder="role (REVENUE)"
                      value={l.accountRole} onChange={e => setLine(i, { accountRole: e.target.value })} title="Engine resolves this role → account via Posting Accounts" />
                  )}
                  <select className={cn(inputCls, 'col-span-1')} value={l.iterator ?? ''} onChange={e => setLine(i, { iterator: e.target.value })}>{ITERATORS.map(it => <option key={it} value={it}>{it || '—'}</option>)}</select>
                  <input className={cn(inputCls, 'col-span-2')} placeholder="include when (opt)" value={l.includeWhen ?? ''} onChange={e => setLine(i, { includeWhen: e.target.value })} />
                  <button onClick={() => set({ lines: h.lines.filter((_, j) => j !== i) })} className="col-span-1 p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded justify-self-center"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-400">Columns: Dr/Cr · amount (field or formula) · account source · role (or account code for a FIXED line) · for-each iterator · include-when. Save, then use “Test a rule” on the list to preview.</p>

            {/* Formula / field reference */}
            <button type="button" onClick={() => setShowRef(v => !v)}
              className="mt-3 text-xs font-medium text-signal hover:underline flex items-center gap-1">
              <HelpCircle size={13} /> {showRef ? 'Hide' : 'Show'} fields &amp; functions
            </button>
            {showRef && (
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-3">
                {FORMULA_REFERENCE.map(g => (
                  <div key={g.group}>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">{g.group}</div>
                    <ul className="space-y-0.5">
                      {g.items.map(it => <li key={it}><code className="text-xs text-slate-600 dark:text-slate-300">{it}</code></li>)}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Typeahead sources for the line editor */}
      <datalist id="rb-amounts">{AMOUNT_BASES.map(a => <option key={a} value={a} />)}</datalist>
      <datalist id="rb-roles">{RULE_ROLES.map(r => <option key={r} value={r} />)}</datalist>
      <datalist id="rb-accounts">{accounts.map(a => <option key={a.id} value={a.code}>{a.code} — {a.name}</option>)}</datalist>
    </div>
  );
};
